import clsx from 'clsx';
import {
  Boxes,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Layers,
  ListOrdered,
  LogOut,
  Menu,
  MoreHorizontal,
  Percent,
  Phone,
  ScrollText,
  Server,
  Settings as SettingsIcon,
  ShoppingCart,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { Button } from './Form';

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
};

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Обзор', icon: LayoutDashboard, end: true },
  { to: '/queue', label: 'Очередь', icon: ListOrdered },
  { to: '/purchases', label: 'Покупки', icon: ShoppingCart },
  { to: '/numbers', label: 'Номера', icon: Phone },
  { to: '/clients', label: 'Клиенты', icon: Users },
  { to: '/pricing', label: 'Цены', icon: Percent },
  { to: '/catalog', label: 'Каталог', icon: Boxes },
  { to: '/pool', label: 'Пул', icon: Layers },
  { to: '/settings', label: 'Настройки', icon: SettingsIcon },
  { to: '/upstream', label: 'Аккаунты', icon: Server },
  { to: '/logs', label: 'Логи', icon: ScrollText },
];

const BOTTOM_NAV = NAV_ITEMS.slice(0, 4);

const COLLAPSE_KEY = 'admin.sidebar.collapsed';

function NavList({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    <nav className="scroll-thin flex-1 space-y-0.5 overflow-y-auto px-2 py-2">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              collapsed && 'justify-center px-2',
              isActive
                ? 'bg-sky-600/15 text-sky-300'
                : 'text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100',
            )
          }
        >
          <item.icon className="size-4 shrink-0" />
          {collapsed ? null : <span className="truncate">{item.label}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand({ collapsed }: { collapsed: boolean }) {
  return (
    <div className={clsx('flex h-14 items-center gap-2 border-b border-zinc-800 px-4', collapsed && 'px-2 justify-center')}>
      <span className="grid size-7 shrink-0 place-items-center rounded-md bg-sky-600 text-xs font-bold text-white">
        SR
      </span>
      {collapsed ? null : (
        <span className="truncate text-sm font-semibold text-zinc-100">SMS Resale</span>
      )}
    </div>
  );
}

export function Layout() {
  const location = useLocation();
  const { session, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => window.localStorage.getItem(COLLAPSE_KEY) === '1',
  );

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const current =
    NAV_ITEMS.find((item) =>
      item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
    ) ?? NAV_ITEMS[0];

  return (
    <div className="min-h-screen bg-zinc-950">
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-zinc-800 bg-zinc-950 lg:flex',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <Brand collapsed={collapsed} />
        <NavList collapsed={collapsed} />
        <div className="border-t border-zinc-800 p-2">
          <Button
            size="sm"
            variant="ghost"
            className="w-full"
            onClick={() => setCollapsed((value) => !value)}
            icon={collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
          >
            {collapsed ? '' : 'Свернуть'}
          </Button>
        </div>
      </aside>

      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Закрыть меню"
            className="absolute inset-0 bg-black/70"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative flex h-full w-72 max-w-[85vw] flex-col border-r border-zinc-800 bg-zinc-950">
            <div className="flex items-center justify-between border-b border-zinc-800 pr-2">
              <div className="flex-1">
                <Brand collapsed={false} />
              </div>
              <button
                type="button"
                aria-label="Закрыть меню"
                onClick={() => setDrawerOpen(false)}
                className="rounded-md p-2 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              >
                <X className="size-4" />
              </button>
            </div>
            <NavList collapsed={false} onNavigate={() => setDrawerOpen(false)} />
            <div className="border-t border-zinc-800 p-3">
              <div className="mb-2 truncate text-xs text-zinc-500">{session?.username}</div>
              <Button
                size="sm"
                variant="secondary"
                className="w-full"
                icon={<LogOut className="size-3.5" />}
                onClick={() => void logout()}
              >
                Выйти
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className={clsx('flex min-h-screen flex-col', collapsed ? 'lg:pl-16' : 'lg:pl-60')}>
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-zinc-800 bg-zinc-950/95 px-3 backdrop-blur sm:px-4">
          <button
            type="button"
            aria-label="Меню"
            onClick={() => setDrawerOpen(true)}
            className="rounded-md p-2 text-zinc-300 hover:bg-zinc-800 lg:hidden"
          >
            <Menu className="size-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-zinc-100">{current.label}</div>
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <span className="max-w-40 truncate text-xs text-zinc-500">{session?.username}</span>
            <Button size="sm" variant="ghost" icon={<LogOut className="size-3.5" />} onClick={() => void logout()}>
              Выйти
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 space-y-4 px-3 py-4 pb-24 sm:px-4 lg:pb-8">
          <Outlet />
        </main>

        <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-zinc-800 bg-zinc-950/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
          {BOTTOM_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                clsx(
                  'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium',
                  isActive ? 'text-sky-400' : 'text-zinc-500',
                )
              }
            >
              <item.icon className="size-5" />
              {item.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium text-zinc-500"
          >
            <MoreHorizontal className="size-5" />
            Ещё
          </button>
        </nav>
      </div>
    </div>
  );
}
